import { DateTime, Interval } from 'luxon';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { CampaignListItem, CampaignStatus } from 'modules/campaigns/types';

import CampaignOverlapAlertMessage from './CampaignOverlapAlertMessage';
import { CampaignOverlapInfo } from './types';

const isScheduleIntersects = (campaign: CampaignListItem, timezone: string, startDate: string, endDate: string) => {
  if (campaign.endDate) {
    const existingCampaignSchedule = Interval.fromDateTimes(
      DateTime.fromISO(campaign.startDate).setZone(timezone),
      DateTime.fromISO(campaign.endDate).setZone(timezone),
    );
    const newCampaignSchedule = Interval.fromDateTimes(
      DateTime.fromISO(startDate).setZone(timezone),
      DateTime.fromISO(endDate).setZone(timezone),
    );

    return existingCampaignSchedule.intersection(newCampaignSchedule);
  }

  const existingInfiniteCampaignStartDate = DateTime.fromISO(campaign.startDate).setZone(timezone);
  const newCampaignEndDate = DateTime.fromISO(endDate).setZone(timezone);

  return existingInfiniteCampaignStartDate.diff(newCampaignEndDate).milliseconds < 0;
};

const getParallelVenueCampaigns = ({
  campaignOverlapInfo: {
    existingCampaigns,
    newCampaignType,
    newCampaignSchedule: { endDate, startDate },
  },
  venueId,
  timezone,
}: {
  campaignOverlapInfo: CampaignOverlapInfo;
  venueId: string;
  timezone: string;
}) =>
  existingCampaigns.filter(
    campaign =>
      campaign.venueIds.includes(venueId) &&
      campaign.campaignType === newCampaignType &&
      isScheduleIntersects(campaign, timezone, startDate, endDate),
  );

const getAlertTextId = (venuesCampaigns: CampaignListItem[]) => {
  const hasScheduledCampaigns = venuesCampaigns.some(campaign => campaign.status === CampaignStatus.SCHEDULED);
  switch (venuesCampaigns.length) {
    case 0:
      return null;
    case 1:
      return hasScheduledCampaigns
        ? 'campaigns.form.venues.venue-has-campaign-scheduled'
        : 'campaigns.form.venues.venue-has-campaign-running';
    default:
      return hasScheduledCampaigns
        ? 'campaigns.form.venues.venue-has-multiple-campaigns-scheduled'
        : 'campaigns.form.venues.venue-has-multiple-campaigns-running';
  }
};

const getVenueAlertMessage = ({
  venueId,
  campaignOverlapInfo,
  inactiveVenuesIds,
  timezone,
}: {
  venueId: string;
  campaignOverlapInfo: CampaignOverlapInfo | undefined;
  inactiveVenuesIds: string[] | undefined;
  timezone: string;
}) => {
  if (campaignOverlapInfo) {
    const parallelVenueCampaigns = getParallelVenueCampaigns({
      venueId,
      timezone,
      campaignOverlapInfo,
    });

    const parallelCampaignsAlertId = getAlertTextId(parallelVenueCampaigns);
    if (parallelCampaignsAlertId) {
      return (
        <CampaignOverlapAlertMessage
          parallelVenueCampaigns={parallelVenueCampaigns}
          parallelCampaignsAlertId={parallelCampaignsAlertId}
          venueId={venueId}
        />
      );
    }
  }
  if (inactiveVenuesIds?.includes(venueId)) {
    return <FormattedMessage id="venue-availability-status.tooltip.venue-status.inactive" />;
  }
  return null;
};

export default getVenueAlertMessage;
